import { Component, computed, DestroyRef, effect, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ListingChannelComparisonService } from 'listings/services/listing-channel-comparison.service';
import { interval } from 'rxjs';
import { ChannelListingMapping } from 'settings/interfaces/channel-listing-mapping.interface';
import {
    ChannelSnapshot,
    ListingSnapshotStatusEnum,
    SnapshotGenerationStalenessEnum,
    SnapshotGenerationType,
} from 'settings/interfaces/listing-channel-shanpshot.interface';
import { DataGridColoredPillCellRendererComponent } from 'shared/components/data-grid/data-grid-colored-pill-cell-renderer/data-grid-colored-pill-cell-renderer.component';
import { DataGridDateCellRendererComponent } from 'shared/components/data-grid/data-grid-date-cell-renderer/data-grid-date-cell-renderer.component';
import { DataGridComponent } from 'shared/components/data-grid/data-grid.component';
import { DefaultDataGridActionCellRendererComponent } from 'shared/components/data-grid/default-data-grid-action-cell-renderer/default-data-grid-action-cell-renderer.component';
import { ZERO_DATE } from 'shared/constants/general-constants';
import { ColoredPillTypesEnum } from 'shared/enums/colored-pill-types.enum';
import { DateComparisonType } from 'shared/enums/dates.enum';
import { OutletTypesEnum } from 'shared/enums/outlet-types.enum';
import { DataGridColumnDefinition } from 'shared/interfaces/data-grid.interface';
import { EventBusService, ToastService } from 'shared/services';
import { compareDateWithDaysAgo } from 'shared/util/dates.utils';
import { pascalToCamelCaseRecursive } from 'shared/util/pascal-to-camel-case';

@Component({
    selector: 'app-listing-user-preferences-channel-comparison',
    templateUrl: './listing-user-preferences-channel-comparison.component.html',
    styleUrls: ['./listing-user-preferences-channel-comparison.component.scss'],
    imports: [TranslateModule, ReactiveFormsModule, DataGridComponent],
})
export class ListingUserPreferencesChannelComparisonComponent implements OnInit {
    private currentTime = signal(new Date());

    public data!: {
        listingId: string;
        outletId: string;
        outletTypeId: OutletTypesEnum;
        closeModal?: (data?: any) => void;
    };

    public listingId = signal<string | null>(null);
    public outletId = signal<string | null>(null);

    public liveSnapshotStatuses = signal<Pick<ChannelSnapshot, 'channelId' | 'status'>[]>([]);
    public snapshotStatuses = computed(() => {
        const liveStatuses = this.liveSnapshotStatuses();
        return this.listingChannelComparisonService.channelSnapshots().map((snapshot) => {
            const liveStatus = liveStatuses.find((localStatus) => localStatus.channelId === snapshot.channelId);
            if (liveStatus) {
                return { channelId: snapshot.channelId, status: liveStatus.status };
            }
            return { channelId: snapshot.channelId, status: snapshot.status };
        });
    });

    public userPreferencesForm = new UntypedFormGroup({
        listingRestrictiveUIType: new UntypedFormControl(null, Validators.required),
        listingRestrictiveUISelectedChannel: new UntypedFormControl(null),
    });

    public channelComparisonColumns: DataGridColumnDefinition[] = [
        {
            headerLabel: 'listings.userPreferences.channelComparison.table.channelName.label',
            dataKey: 'channelName',
        } as DataGridColumnDefinition,
        {
            headerLabel: 'listings.userPreferences.channelComparison.table.lastSnapshotDate.label',
            dataKey: 'createdAt',
            customCellRenderer: DataGridDateCellRendererComponent,
            customCellRendererData: {
                tooltip: {
                    text: (value: string) => {
                        if (!value || value === ZERO_DATE) {
                            return 'listings.userPreferences.channelComparison.table.lastSnapshotStaleness.pill.missing.tooltip';
                        }
                        return compareDateWithDaysAgo(value, 1, DateComparisonType.OLDER)
                            ? 'listings.userPreferences.channelComparison.table.lastSnapshotStaleness.pill.stale.tooltip'
                            : 'listings.userPreferences.channelComparison.table.lastSnapshotStaleness.pill.fresh.tooltip';
                    },
                    position: 'top',
                    trigger: 'hover',
                },
                customCellRendererData: {
                    format: 'yyyy-MM-dd HH:mm:ss',
                },
                valueFormat: (createdAt: string) =>
                    !createdAt || createdAt === ZERO_DATE
                        ? 'listings.userPreferences.channelComparison.table.lastSnapshotDate.missing.label'
                        : new Date(createdAt).toLocaleString(),
            },
            cellClass: 'createdAt-snapshotDate',
        } as DataGridColumnDefinition,
        {
            headerLabel: 'listings.userPreferences.channelComparison.table.lastSnapshotStaleness.label',
            dataKey: 'dateStale',
            customCellRenderer: DataGridColoredPillCellRendererComponent,
            customCellRendererData: {
                pillType: (value: SnapshotGenerationStalenessEnum) => {
                    switch (value) {
                        case SnapshotGenerationStalenessEnum.MISSING:
                            return ColoredPillTypesEnum.INFO;
                        case SnapshotGenerationStalenessEnum.STALE:
                            return ColoredPillTypesEnum.WARNING;
                        case SnapshotGenerationStalenessEnum.FRESH:
                            return ColoredPillTypesEnum.SUCCESS;
                        default:
                            return ColoredPillTypesEnum.INFO;
                    }
                },
                valueFormat: (value: SnapshotGenerationStalenessEnum) => {
                    switch (value) {
                        case SnapshotGenerationStalenessEnum.MISSING:
                            return 'listings.userPreferences.channelComparison.table.lastSnapshotStaleness.pill.missing.label';
                        case SnapshotGenerationStalenessEnum.STALE:
                            return 'listings.userPreferences.channelComparison.table.lastSnapshotStaleness.pill.stale.label';
                        case SnapshotGenerationStalenessEnum.FRESH:
                            return 'listings.userPreferences.channelComparison.table.lastSnapshotStaleness.pill.fresh.label';
                        default:
                            return 'listings.userPreferences.channelComparison.table.lastSnapshotStaleness.pill.missing.label';
                    }
                },
                tooltip: {
                    text: (value: SnapshotGenerationStalenessEnum) => {
                        switch (value) {
                            case SnapshotGenerationStalenessEnum.MISSING:
                                return 'listings.userPreferences.channelComparison.table.lastSnapshotStaleness.pill.missing.tooltip';
                            case SnapshotGenerationStalenessEnum.STALE:
                                return 'listings.userPreferences.channelComparison.table.lastSnapshotStaleness.pill.stale.tooltip';
                            case SnapshotGenerationStalenessEnum.FRESH:
                                return 'listings.userPreferences.channelComparison.table.lastSnapshotStaleness.pill.fresh.tooltip';
                            default:
                                return 'listings.userPreferences.channelComparison.table.lastSnapshotStaleness.pill.missing.tooltip';
                        }
                    },
                    position: 'top',
                    trigger: 'hover',
                },
            },
        } as DataGridColumnDefinition,
        {
            headerLabel: 'listings.userPreferences.channelComparison.table.snapshotStatus.label',
            dataKey: 'status',
            customCellRenderer: DataGridColoredPillCellRendererComponent,
            customCellRendererData: {
                pillType: (value: string) => {
                    const numericValue = Number(value);
                    switch (numericValue) {
                        case ListingSnapshotStatusEnum.AVAILABLE:
                            return ColoredPillTypesEnum.SUCCESS;
                        case ListingSnapshotStatusEnum.FAILED:
                            return ColoredPillTypesEnum.WARNING;
                        case ListingSnapshotStatusEnum.ROLLEDBACK:
                            return ColoredPillTypesEnum.WARNING;
                        case ListingSnapshotStatusEnum.REGENERATING:
                        case ListingSnapshotStatusEnum.MISSING:
                        default:
                            return ColoredPillTypesEnum.INFO;
                    }
                },
                valueFormat: (value: ListingSnapshotStatusEnum) => {
                    switch (value) {
                        case ListingSnapshotStatusEnum.AVAILABLE:
                            return 'listings.userPreferences.channelComparison.table.snapshotStatus.pill.available.label';
                        case ListingSnapshotStatusEnum.FAILED:
                            return 'listings.userPreferences.channelComparison.table.snapshotStatus.pill.failed.label';
                        case ListingSnapshotStatusEnum.ROLLEDBACK:
                            return 'listings.userPreferences.channelComparison.table.snapshotStatus.pill.rolledback.label';
                        case ListingSnapshotStatusEnum.REGENERATING:
                            return 'listings.userPreferences.channelComparison.table.snapshotStatus.pill.regenerating.label';
                        case ListingSnapshotStatusEnum.MISSING:
                        default:
                            return 'listings.userPreferences.channelComparison.table.snapshotStatus.pill.missing.label';
                    }
                },
                tooltip: {
                    text: (value: string) => {
                        const numericValue = Number(value);
                        switch (numericValue) {
                            case ListingSnapshotStatusEnum.AVAILABLE:
                                return 'listings.userPreferences.channelComparison.table.snapshotStatus.pill.available.tooltip';
                            case ListingSnapshotStatusEnum.FAILED:
                                return 'listings.userPreferences.channelComparison.table.snapshotStatus.pill.failed.tooltip';
                            case ListingSnapshotStatusEnum.ROLLEDBACK:
                                return 'listings.userPreferences.channelComparison.table.snapshotStatus.pill.rolledback.tooltip';
                            case ListingSnapshotStatusEnum.REGENERATING:
                                return 'listings.userPreferences.channelComparison.table.snapshotStatus.pill.regenerating.tooltip';
                            case ListingSnapshotStatusEnum.MISSING:
                            default:
                                return 'listings.userPreferences.channelComparison.table.snapshotStatus.pill.missing.tooltip';
                        }
                    },
                    position: 'top',
                    trigger: 'hover',
                },
            },
        } as DataGridColumnDefinition,
        {
            headerLabel: 'listings.userPreferences.channelComparison.table.generateAction.label',
            dataKey: 'generateAction',
            customCellRenderer: DefaultDataGridActionCellRendererComponent,
            customCellRendererData: [
                {
                    label: (rowData: any) => this.getGenerateActionButtonLabel(rowData),
                    icon: (rowData: any) => this.getGenerateActionButtonIcon(rowData),
                    clickHandler: this.handleChannelAction.bind(this),
                    disabled: this.computeGenerateActionDisabledState.bind(this),
                    showLoader: this.computeGenerateActionLoadingState.bind(this),
                },
            ],
        } as DataGridColumnDefinition,
        {
            headerLabel: 'listings.userPreferences.channelComparison.table.comparisonAction.label',
            dataKey: 'comparisonAction',
            customCellRenderer: DefaultDataGridActionCellRendererComponent,
            customCellRendererData: [
                {
                    label: this.getStartComparisonButtonLabel.bind(this),
                    icon: 'play',
                    clickHandler: this.handleStartComparisonAction.bind(this),
                    disabled: this.computeStartComparisonActionDisabledState.bind(this),
                },
            ],
        } as DataGridColumnDefinition,
    ];

    constructor(
        public listingChannelComparisonService: ListingChannelComparisonService,
        private toastService: ToastService,
        private eventBusService: EventBusService,
        private destroyRef: DestroyRef,
    ) {
        effect(() => {
            const outletId = this.outletId();
            const listingId = this.listingId();

            if (outletId && this.listingChannelComparisonService.outletId() !== outletId) {
                this.listingChannelComparisonService.outletId.set(outletId);
                this.listingChannelComparisonService.outletType.set(this.data.outletTypeId ?? OutletTypesEnum.HOTEL);
            }
            if (listingId && this.listingChannelComparisonService.listingId() !== listingId) {
                this.listingChannelComparisonService.listingId.set(listingId);
            }
        });
    }

    ngOnInit() {
        this.listingChannelComparisonService.resetService();
        this.listingId.set(this.data?.listingId ?? null);
        this.outletId.set(this.data?.outletId ?? null);

        interval(3000)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.currentTime.set(new Date());
            });

        this.eventBusService
            .getObservableForEventStream('listing:snapshotStatusUpdate')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event: any) => {
                const statusUpdate = pascalToCamelCaseRecursive(JSON.parse(event)) as any;
                if (statusUpdate.listingId === this.listingId()) {
                    this.updateLiveSnapshotStatus(statusUpdate.channelId, statusUpdate.status);

                    if (statusUpdate.status !== ListingSnapshotStatusEnum.REGENERATING) {
                        this.listingChannelComparisonService.refreshChannelSnapshot$.next(statusUpdate.channelId);
                        this.removeLiveSnapshotStatus(statusUpdate.channelId);
                    }
                }
            });
    }

    hasEnoughTimePassedForNewSnapshot(createdAt: string): boolean {
        if (!createdAt || createdAt === ZERO_DATE || createdAt === '-') {
            return true;
        }

        const snapshotDate = new Date(createdAt);
        const now = this.currentTime();
        const fifteenMinutesInMs = 15 * 60 * 1000;
        const timeDifference = now.getTime() - snapshotDate.getTime();

        return timeDifference >= fifteenMinutesInMs;
    }

    saveListingChannelComparison() {
        if (this.userPreferencesForm.invalid) {
            this.userPreferencesForm.markAllAsTouched();
            return;
        }
    }

    handleChannelAction(rowData: ChannelListingMapping) {
        let snapshotGenerationType = SnapshotGenerationType.UNKNOWN;

        // Identifying channels for special matching rules by channel name (only way to tell which channel is which)
        if (rowData.channelName?.toLowerCase() === 'booking') {
            snapshotGenerationType = SnapshotGenerationType.BOOKING_RPA;
        }

        if (rowData.channelName?.toLowerCase() === 'expedia') {
            snapshotGenerationType = SnapshotGenerationType.EXPEDIA_RPA;
        }

        this.listingChannelComparisonService
            .enqueueSnapshotGeneration(this.listingId(), rowData.channelId, snapshotGenerationType)
            .subscribe({
                next: () => {
                    this.toastService.showToast({
                        type: 'simple',
                        body: 'listings.userPreferences.channelComparison.toast.snapshotGenerationEnqueue.success',
                        severity: 'info',
                        duration: 5000,
                        listType: 'none',
                    });

                    this.updateLiveSnapshotStatus(rowData.channelId, ListingSnapshotStatusEnum.REGENERATING);
                },
                error: () => {
                    this.toastService.showToast({
                        type: 'simple',
                        body: 'listings.userPreferences.channelComparison.toast.snapshotGenerationEnqueue.fail',
                        severity: 'error',
                        duration: 5000,
                        listType: 'none',
                    });
                },
            });
    }

    handleStartComparisonAction(
        rowData: Pick<ChannelSnapshot, 'id' | 'listingId' | 'channelId' | 'status' | 'createdAt'> &
            Pick<ChannelListingMapping, 'channelName'>,
    ) {
        const rowStatus = this.getCurrentStatusForChannel(rowData.channelId, rowData.status);

        if (rowStatus === ListingSnapshotStatusEnum.AVAILABLE || rowStatus === ListingSnapshotStatusEnum.ROLLEDBACK) {
            this.listingChannelComparisonService.activeSnapshotId.set(rowData.id);
            this.data.closeModal?.(undefined);
        }
    }

    getGenerateActionButtonLabel(rowData: any): string {
        const rowStatus = this.getCurrentStatusForChannel(rowData.channelId, rowData.status);

        switch (rowStatus) {
            case ListingSnapshotStatusEnum.ROLLEDBACK:
                return 'listings.userPreferences.channelComparison.table.generateAction.rolledback.label';
            case ListingSnapshotStatusEnum.FAILED:
            case ListingSnapshotStatusEnum.AVAILABLE:
                if (!this.hasEnoughTimePassedForNewSnapshot(rowData.createdAt)) {
                    return 'listings.userPreferences.channelComparison.table.generateAction.tooltip.notEnoughTimePased';
                }
                return 'listings.userPreferences.channelComparison.table.generateAction.previousSnapshotExists.label';
            case ListingSnapshotStatusEnum.REGENERATING:
                return 'listings.userPreferences.channelComparison.table.generateAction.generationInProgress.label';
            case ListingSnapshotStatusEnum.MISSING:
            default:
                return 'listings.userPreferences.channelComparison.table.generateAction.label';
        }
    }

    getStartComparisonButtonLabel(rowData: any): string {
        const rowStatus = this.getCurrentStatusForChannel(rowData.channelId, rowData.status);

        switch (rowStatus) {
            case ListingSnapshotStatusEnum.REGENERATING:
                return 'listings.userPreferences.channelComparison.table.comparisonAction.generationInProgress.label';
            case ListingSnapshotStatusEnum.AVAILABLE:
            case ListingSnapshotStatusEnum.ROLLEDBACK:
                return 'listings.userPreferences.channelComparison.table.comparisonAction.start.label';
            case ListingSnapshotStatusEnum.MISSING:
            case ListingSnapshotStatusEnum.FAILED:
            default:
                return 'listings.userPreferences.channelComparison.table.comparisonAction.snapshotUnavailable.label';
        }
    }

    getGenerateActionButtonIcon(rowData: any): string {
        const rowStatus = this.getCurrentStatusForChannel(rowData.channelId, rowData.status);

        switch (rowStatus) {
            case ListingSnapshotStatusEnum.ROLLEDBACK:
            case ListingSnapshotStatusEnum.FAILED:
            case ListingSnapshotStatusEnum.AVAILABLE:
                return 'arrow-rotate-right';
            case ListingSnapshotStatusEnum.MISSING:
                return 'plus';
            case ListingSnapshotStatusEnum.REGENERATING:
                return 'arrows-rotate';
            default:
                return '';
        }
    }

    computeGenerateActionLoadingState(rowData: any) {
        return this.getCurrentStatusForChannel(rowData.channelId, rowData.status) === ListingSnapshotStatusEnum.REGENERATING;
    }

    computeGenerateActionDisabledState(rowData: any): boolean {
        const status = this.getCurrentStatusForChannel(rowData.channelId, rowData.status);

        // Disable if rolledback
        if (status === ListingSnapshotStatusEnum.ROLLEDBACK) {
            return true;
        }

        // Disable if regenerating
        if (status === ListingSnapshotStatusEnum.REGENERATING) {
            return true;
        }

        // Disable if AVAILABLE or FAILED but 15 minutes cooldown hasn't passed
        if (status === ListingSnapshotStatusEnum.AVAILABLE || status === ListingSnapshotStatusEnum.FAILED) {
            return !this.hasEnoughTimePassedForNewSnapshot(rowData.createdAt);
        }

        return false;
    }

    computeStartComparisonActionDisabledState(rowData: any): boolean {
        const status = this.getCurrentStatusForChannel(rowData.channelId, rowData.status);
        return status !== ListingSnapshotStatusEnum.AVAILABLE && status !== ListingSnapshotStatusEnum.ROLLEDBACK;
    }

    private getCurrentStatusForChannel(channelId: string, rowStatus?: ListingSnapshotStatusEnum): ListingSnapshotStatusEnum | undefined {
        const liveStatus = this.liveSnapshotStatuses().find((status) => status.channelId === channelId);
        if (liveStatus) {
            return liveStatus.status;
        }
        return rowStatus;
    }

    private updateLiveSnapshotStatus(channelId: string, status: ListingSnapshotStatusEnum): void {
        this.liveSnapshotStatuses.update((statuses) => {
            const existingIndex = statuses.findIndex((s) => s.channelId === channelId);
            const newStatus = { channelId, status };

            return existingIndex === -1
                ? [...statuses, newStatus]
                : statuses.map((statusItem, index) => (index === existingIndex ? newStatus : statusItem));
        });
    }

    private removeLiveSnapshotStatus(channelId: string) {
        const indexToRemove = this.liveSnapshotStatuses().findIndex((status) => status.channelId === channelId);
        if (indexToRemove !== -1) {
            this.liveSnapshotStatuses.update((currentStatuses) => {
                return currentStatuses.filter((status) => status.channelId !== channelId);
            });
        }
    }
}
